import React, { useState, useMemo } from 'react';
import { Search, UserPlus, X, Users, Shield, ShieldCheck } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { TeamNewFormValues, NewReferenceData } from '../types/teamNew.types';

interface MembersStepProps {
  form: TeamNewFormValues;
  onChange: (patch: Partial<TeamNewFormValues>) => void;
  referenceData: NewReferenceData;
}

export function MembersStep({ form, onChange, referenceData }: MembersStepProps) {
  const [search, setSearch] = useState('');

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return [];
    return referenceData.users.filter(user => 
      user.label.toLowerCase().includes(search.toLowerCase()) &&
      !form.members.includes(user.id) &&
      user.id !== form.leader_user_id
    );
  }, [search, referenceData.users, form.members, form.leader_user_id]);

  const selectedUsers = useMemo(() => {
    return referenceData.users.filter(user => form.members.includes(user.id));
  }, [referenceData.users, form.members]);

  const handleAddMember = (userId: string) => {
    onChange({ members: [...form.members, userId] });
    setSearch('');
  };

  const handleRemoveMember = (userId: string) => {
    onChange({ members: form.members.filter(id => id !== userId) });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Tìm kiếm & Gợi ý */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-md border border-slate-100 p-4 space-y-4">
            <h3 className="sm:text-sm text-xs font-normal text-slate-900 flex items-center gap-2">
              <Search className="w-4 h-4 text-emerald-600" />
              Tìm kiếm nhân sự
            </h3>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Nhập tên nhân sự..."
                className="w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-md sm:text-sm text-xs outline-none focus:ring-1 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Kết quả tìm kiếm */}
            <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
              {search.trim() && filteredUsers.length === 0 && (
                <div className="py-8 text-center">
                  <p className="sm:text-sm text-xs text-slate-400 italic">Không tìm thấy nhân sự nào phù hợp.</p>
                </div>
              )}
              
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleAddMember(user.id)}
                  className="w-full flex items-center justify-between p-2 rounded-md hover:bg-slate-50 group transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center sm:text-sm text-xs font-normal border border-emerald-100">
                      {user.label[0]}
                    </div>
                    <span className="sm:text-sm text-xs text-slate-700">{user.label}</span>
                  </div>
                  <UserPlus className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 transition-colors" />
                </button>
              ))}
            </div>
          </div>

          <div className="bg-emerald-50/50 rounded-md border border-emerald-100 p-4">
            <h4 className="sm:text-sm text-xs text-emerald-600 tracking-widest font-normal mb-3 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              Quy tắc phân bổ
            </h4>
            <ul className="space-y-2">
              <li className="sm:text-sm text-xs text-slate-600 flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                Trưởng team đã được chọn ở bước trước.
              </li>
              <li className="sm:text-sm text-xs text-slate-600 flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                Một nhân sự có thể tham gia nhiều team nhưng chỉ làm trưởng 1 team.
              </li>
            </ul>
          </div>
        </div>

        {/* Right Column: Danh sách thành viên đã chọn */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-md border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="sm:text-sm text-xs font-normal text-slate-900 flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-600" />
                Danh sách thành viên ({form.members.length})
              </h3>
              {form.members.length > 0 && (
                <button 
                  onClick={() => onChange({ members: [] })}
                  className="sm:text-sm text-xs text-rose-500 hover:text-rose-600 font-normal tracking-wider transition-colors"
                >
                  Xóa tất cả
                </button>
              )}
            </div>

            {form.members.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-slate-50/50 rounded-md border border-dashed border-slate-200">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 text-slate-300 border border-slate-100">
                  <Users className="w-6 h-6" />
                </div>
                <p className="sm:text-sm text-xs text-slate-500">Chưa có thành viên nào được chọn</p>
                <p className="sm:text-sm text-xs text-slate-400 mt-1">Sử dụng thanh tìm kiếm bên trái để thêm thành viên vào team.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedUsers.map((user) => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-3 rounded-md border border-slate-100 hover:border-emerald-100 hover:bg-emerald-50/20 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center sm:text-sm text-xs font-normal border border-blue-100">
                        {user.label[0]}
                      </div>
                      <div>
                        <div className="sm:text-sm text-xs text-slate-900 font-normal">{user.label}</div>
                        <div className="sm:text-sm text-xs text-slate-400 font-normal">Thành viên chính thức</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveMember(user.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-md text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100"
                      title="Xóa khỏi team"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Info Card */}
          <div className="bg-slate-900 rounded-md p-6 text-white overflow-hidden relative">
            <div className="relative z-10 flex items-center justify-between">
              <div className="space-y-1">
                <h4 className="sm:text-sm text-xs font-normal opacity-90">Năng lực tổ đội ước tính</h4>
                <p className="sm:text-sm text-xs opacity-60">Dựa trên số lượng thành viên và định mức cơ bản</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-light text-emerald-400">
                  {form.members.length * (form.base_capacity || 8)} <span className="sm:text-sm text-xs opacity-60 text-white">công/ngày</span>
                </div>
              </div>
            </div>
            {/* Abstract Background element */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
